import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface OAuthProvider {
  name: string;
  displayName: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  authUrl: string;
  icon: string;
}

export interface OAuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  provider: string;
}

@Injectable({
  providedIn: 'root'
})
export class OAuthService {
  private apiUrl = '/auth';

  /** Mirrors backend `ENABLE_SSO` (`/auth/oauth/config` → `sso_enabled`). */
  private ssoEnabled = false;

  /** Google + Microsoft only (Apple/Facebook removed from product UI). */
  private providers: { [key: string]: OAuthProvider } = {
    google: {
      name: 'google',
      displayName: 'Google',
      clientId: '',
      redirectUri: `${window.location.origin}/auth/oauth/google/callback`,
      scope: 'openid email profile',
      authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      icon: 'google'
    },
    microsoft: {
      name: 'microsoft',
      displayName: 'Microsoft',
      clientId: '',
      redirectUri: `${window.location.origin}/auth/oauth/microsoft/callback`,
      scope: 'openid email profile',
      authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      icon: 'microsoft'
    }
  };

  constructor(
    private http: HttpClient
  ) {
    this.loadProviderConfigs();
  }

  /**
   * Load OAuth provider configurations from backend
   */
  private loadProviderConfigs(): Promise<void> {
    return this.http.get<{
      sso_enabled?: boolean;
      providers: { [key: string]: Partial<OAuthProvider> };
    }>(`${this.apiUrl}/oauth/config`)
      .pipe(
        catchError((error) => {
          console.warn('Failed to load OAuth configurations, using defaults', error);
          this.ssoEnabled = false;
          return of({ sso_enabled: false, providers: {} });
        })
      )
      .toPromise()
      .then(response => {
        const enabled = response?.sso_enabled === true;
        this.ssoEnabled = enabled;

        if (!enabled) {
          Object.keys(this.providers).forEach(name => {
            this.providers[name] = { ...this.providers[name], clientId: '' };
          });
          return;
        }
        if (response?.providers) {
          Object.keys(this.providers).forEach(provider => {
            if (response.providers[provider]) {
              this.providers[provider] = { ...this.providers[provider], ...response.providers[provider] };
            }
          });
        }
      });
  }

  /**
   * Get available OAuth providers
   */
  getProviders(): OAuthProvider[] {
    if (!this.ssoEnabled) {
      return [];
    }
    return Object.values(this.providers).filter(provider => !!provider.clientId);
  }

  /** Whether SSO is allowed by the server (`ENABLE_SSO`). */
  isSsoEnabledByServer(): boolean {
    return this.ssoEnabled;
  }

  /**
   * Initiate OAuth login with specified provider
   */
  initiateOAuthLogin(providerName: string): void {
    if (!this.ssoEnabled) {
      console.warn('SSO is disabled (ENABLE_SSO).');
      return;
    }
    const provider = this.providers[providerName];
    if (!provider) {
      console.error(`OAuth provider ${providerName} not found`);
      return;
    }

    if (!provider.clientId) {
      console.error(`OAuth provider ${providerName} has no client ID`);
      console.log('Available providers:', this.providers);
      return;
    }

    const params = new URLSearchParams({
      client_id: provider.clientId,
      redirect_uri: provider.redirectUri,
      response_type: 'code',
      scope: provider.scope,
      state: this.generateState()
    });

    // Store state for validation
    localStorage.setItem('oauth_state', params.get('state')!);
    localStorage.setItem('oauth_provider', providerName);

    const authUrl = `${provider.authUrl}?${params.toString()}`;
    console.log('OAuth URL:', authUrl);
    window.location.href = authUrl;
  }

  /**
   * Handle OAuth callback
   * Note: This method is no longer used as the backend now redirects directly
   * The OAuth response is handled by the login component via URL parameters
   */
  handleOAuthCallback(code: string, state: string, provider: string): Observable<any> {
    // This method is kept for compatibility but is no longer used
    // The backend now redirects directly to the frontend with response data
    console.log('OAuth callback received - backend will handle redirect');
    return of({ message: 'OAuth callback handled by backend redirect' });
  }

  /**
   * Handle successful login
   */
  private handleSuccessfulLogin(response: any): void {
    console.log('OAuth - Login response:', response);
    
    if (response.token) {
      // Single user - direct login
      localStorage.setItem('lms_token', response.token);
      const redirectUrl = `/auth/token-login?token=${encodeURIComponent(response.token)}`;
      console.log('OAuth - Redirecting to:', redirectUrl);
      window.location.href = redirectUrl;
    } else if (response.users && response.chooseLC) {
      // Multiple users - redirect to new login page with LC selection data
      const redirectUrl = `/main/auth/new-login?oauth_response=${encodeURIComponent(JSON.stringify(response))}`;
      console.log('OAuth - Redirecting to LC selection:', redirectUrl);
      window.location.href = redirectUrl;
    } else if (response.message) {
      // Error - redirect to new login page with error
      const redirectUrl = `/main/auth/new-login?oauth_error=${encodeURIComponent(response.message)}`;
      console.log('OAuth - Redirecting with error:', redirectUrl);
      window.location.href = redirectUrl;
    }
  }

  /**
   * Generate random state for OAuth security
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Check if OAuth is available
   */
  isOAuthAvailable(): boolean {
    return this.ssoEnabled && this.getProviders().length > 0;
  }

  /**
   * Wait for OAuth config to load
   */
  async waitForConfig(): Promise<void> {
    await this.loadProviderConfigs();
  }
}
